import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Row, Col, Button, Input, Select, Modal, Form, Upload, message, Tag, Space,
  Empty, Pagination, Dropdown, Drawer, List, Tooltip, Popconfirm, InputNumber, Spin, Typography, Image, Switch
} from 'antd';
import {
  PlusOutlined, DownloadOutlined, EditOutlined, DeleteOutlined, EllipsisOutlined,
  HistoryOutlined, CloudUploadOutlined, FilePdfOutlined, FileWordOutlined,
  FileExcelOutlined, FilePptOutlined, FileImageOutlined, FileTextOutlined,
  ClockCircleOutlined, FolderOpenOutlined, FolderOutlined, InboxOutlined,
  SearchOutlined, AppstoreOutlined, LinkOutlined, LockOutlined
} from '@ant-design/icons';
import api from '../utils/axios';
import { useAuth } from '../contexts/AuthContext';

const { Text, Paragraph } = Typography;
const PAGE_SIZE = 12;

// 文件类型与图标/配色映射
const FILE_TYPE_MAP = {
  pdf: { Icon: FilePdfOutlined, color: '#ff4d4f', bg: '#fff2f0' },
  doc: { Icon: FileWordOutlined, color: '#1677ff', bg: '#e6f4ff' },
  docx: { Icon: FileWordOutlined, color: '#1677ff', bg: '#e6f4ff' },
  wps: { Icon: FileWordOutlined, color: '#1677ff', bg: '#e6f4ff' },
  xls: { Icon: FileExcelOutlined, color: '#52c41a', bg: '#f6ffed' },
  xlsx: { Icon: FileExcelOutlined, color: '#52c41a', bg: '#f6ffed' },
  et: { Icon: FileExcelOutlined, color: '#52c41a', bg: '#f6ffed' },
  ppt: { Icon: FilePptOutlined, color: '#fa8c16', bg: '#fff7e6' },
  pptx: { Icon: FilePptOutlined, color: '#fa8c16', bg: '#fff7e6' },
  png: { Icon: FileImageOutlined, color: '#722ed1', bg: '#f9f0ff' },
  jpg: { Icon: FileImageOutlined, color: '#722ed1', bg: '#f9f0ff' },
  jpeg: { Icon: FileImageOutlined, color: '#722ed1', bg: '#f9f0ff' }
};

const getFileTypeMeta = (ext) => FILE_TYPE_MAP[ext] || { Icon: FileTextOutlined, color: '#8c8c8c', bg: '#fafafa' };

// 可直接预览的图片类型
const IMAGE_EXTS = ['png', 'jpg', 'jpeg'];
// 图片缩略图 blob URL 的会话级缓存（翻页/重挂载时复用，避免重复请求）
const thumbBlobCache = new Map();

// 卡片左上角标识：图片文档显示缩略图并支持点击预览，其他类型显示原有图标
const DocThumbnail = ({ doc, fallbackMeta }) => {
  const isImage = IMAGE_EXTS.includes(doc.extension);
  const [src, setSrc] = useState(() => (isImage ? thumbBlobCache.get(doc.id) || null : null));
  const FallbackIcon = fallbackMeta.Icon;

  useEffect(() => {
    if (!isImage || src) return undefined;
    let cancelled = false;
    api.get(`/documents/${doc.id}/download`, { responseType: 'blob' })
      .then((resp) => {
        if (cancelled) return;
        const objectUrl = window.URL.createObjectURL(resp.data);
        thumbBlobCache.set(doc.id, objectUrl);
        setSrc(objectUrl);
      })
      .catch(() => { /* 加载失败保持文件类型图标 */ });
    return () => { cancelled = true; };
  }, [isImage, doc.id, src]);

  if (!isImage || !src) {
    return (
      <div style={{
        width: 44, height: 44, borderRadius: 10, flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: fallbackMeta.bg
      }}>
        <FallbackIcon style={{ fontSize: 24, color: fallbackMeta.color }} />
      </div>
    );
  }

  return (
    <Image
      src={src}
      alt={doc.title}
      width={44}
      height={44}
      style={{ borderRadius: 10, objectFit: 'cover', flexShrink: 0, cursor: 'zoom-in' }}
      preview={{ mask: '点击预览' }}
    />
  );
};

const formatFileSize = (size) => {
  if (!size && size !== 0) return '';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / 1024 / 1024).toFixed(2)} MB`;
};

const formatTime = (s) => (s ? String(s).replace('T', ' ').slice(0, 16) : '-');

const ACCEPT_EXT = '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.wps,.et,.txt,.png,.jpg,.jpeg';

const Documents = () => {
  const { user } = useAuth();
  const isAdminUser = user?.role === 'super_admin' || user?.role === 'hr_manager';

  const [categories, setCategories] = useState([]);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [queryParams, setQueryParams] = useState({ page: 1, categoryId: null, keyword: '' });
  const [searchValue, setSearchValue] = useState('');
  const [downloadingId, setDownloadingId] = useState(null);

  // 新建/编辑文档弹窗
  const [docModal, setDocModal] = useState({ open: false, mode: 'create', record: null });
  const [docForm] = Form.useForm();
  const [docFileList, setDocFileList] = useState([]);
  const [docSaving, setDocSaving] = useState(false);

  // 分组管理弹窗
  const [catModalOpen, setCatModalOpen] = useState(false);
  const [catForm] = Form.useForm();
  const [catEditing, setCatEditing] = useState(null);
  const [catSaving, setCatSaving] = useState(false);

  // 版本记录抽屉
  const [versionDrawer, setVersionDrawer] = useState({ open: false, record: null, versions: [], loading: false });

  // 上传新版本弹窗
  const [versionModal, setVersionModal] = useState({ open: false, record: null });
  const [versionForm] = Form.useForm();
  const [versionFileList, setVersionFileList] = useState([]);
  const [versionSaving, setVersionSaving] = useState(false);

  // 加载分组
  const fetchCategories = useCallback(async () => {
    try {
      const resp = await api.get('/documents/categories');
      if (resp.data?.success) setCategories(resp.data.data || []);
    } catch (e) {
      console.error('获取分组失败:', e);
    }
  }, []);

  // 加载文档（服务端分页）
  const fetchDocs = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await api.get('/documents', {
        params: {
          page: queryParams.page,
          limit: PAGE_SIZE,
          category_id: queryParams.categoryId || undefined,
          keyword: queryParams.keyword || undefined
        }
      });
      if (resp.data?.success) {
        setDocs(resp.data.data || []);
        setTotal(resp.data.pagination?.total || 0);
      }
    } catch (e) {
      message.error('获取文档列表失败');
      console.error('获取文档列表失败:', e);
    } finally {
      setLoading(false);
    }
  }, [queryParams]);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);
  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  // 搜索输入 400ms 防抖
  useEffect(() => {
    const timer = setTimeout(() => {
      const keyword = searchValue.trim();
      setQueryParams((prev) => {
        // 值未变化时返回原引用，避免触发多余的请求
        if (prev.keyword === keyword && prev.page === 1) return prev;
        return { ...prev, keyword, page: 1 };
      });
    }, 400);
    return () => clearTimeout(timer);
  }, [searchValue]);

  // 下载（带鉴权头，blob 方式保存）
  const downloadFile = useCallback(async (url, fallbackName) => {
    setDownloadingId(url);
    try {
      const resp = await api.get(url, { responseType: 'blob' });
      let fileName = fallbackName;
      const disposition = resp.headers['content-disposition'];
      if (disposition) {
        const match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
        if (match) fileName = decodeURIComponent(match[1]);
      }
      const blob = new Blob([resp.data], { type: resp.data.type || 'application/octet-stream' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(link.href);
    } catch (e) {
      message.error('下载失败，请稍后重试');
      console.error('下载失败:', e);
    } finally {
      setDownloadingId(null);
    }
  }, []);

  // ---------- 文档新建/编辑 ----------
  const openCreateDoc = () => {
    docForm.resetFields();
    setDocFileList([]);
    setDocModal({ open: true, mode: 'create', record: null });
  };

  const openEditDoc = (record) => {
    docForm.setFieldsValue({
      title: record.title,
      category_id: record.category_id,
      remark: record.remark,
      feishu_url: record.feishu_url
    });
    setDocFileList([]);
    setDocModal({ open: true, mode: 'edit', record });
  };

  const handleDocSubmit = async () => {
    try {
      const values = await docForm.validateFields();
      setDocSaving(true);
      if (docModal.mode === 'create') {
        if (docFileList.length === 0) {
          message.warning('请上传文档文件');
          return;
        }
        const fd = new FormData();
        fd.append('title', values.title);
        fd.append('category_id', values.category_id);
        fd.append('remark', values.remark || '');
        fd.append('feishu_url', values.feishu_url || '');
        fd.append('change_note', values.change_note || '');
        fd.append('file', docFileList[0].originFileObj || docFileList[0]);
        await api.post('/documents', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        message.success('文档创建成功');
      } else {
        await api.put(`/documents/${docModal.record.id}`, {
          title: values.title,
          category_id: values.category_id,
          remark: values.remark || '',
          feishu_url: values.feishu_url || ''
        });
        message.success('文档更新成功');
      }
      setDocModal((m) => ({ ...m, open: false }));
      fetchDocs();
      fetchCategories();
    } catch (e) {
      if (e?.errorFields) return; // 表单校验错误
      message.error(e.response?.data?.message || '保存失败');
      console.error('保存文档失败:', e);
    } finally {
      setDocSaving(false);
    }
  };

  const handleDeleteDoc = (record) => {
    Modal.confirm({
      title: `确认删除文档「${record.title}」？`,
      content: '删除后该文档将在前台隐藏（后台保留数据，可联系技术人员恢复），历史版本文件不会被清除。',
      okText: '确认删除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          await api.delete(`/documents/${record.id}`);
          message.success('文档已删除');
          fetchDocs();
          fetchCategories();
        } catch (e) {
          message.error(e.response?.data?.message || '删除失败');
        }
      }
    });
  };

  // ---------- 分组管理 ----------
  const openCategoryModal = () => {
    catForm.resetFields();
    setCatEditing(null);
    setCatModalOpen(true);
  };

  const startEditCategory = (cat) => {
    setCatEditing(cat);
    catForm.setFieldsValue({
      name: cat.name,
      description: cat.description,
      sort_order: cat.sort_order,
      is_private: !!cat.is_private
    });
  };

  const cancelEditCategory = () => {
    setCatEditing(null);
    catForm.resetFields();
  };

  const handleCategorySubmit = async () => {
    try {
      const values = await catForm.validateFields();
      setCatSaving(true);
      if (catEditing) {
        await api.put(`/documents/categories/${catEditing.id}`, values);
        message.success('分组更新成功');
      } else {
        await api.post('/documents/categories', values);
        message.success('分组创建成功');
      }
      setCatEditing(null);
      catForm.resetFields();
      fetchCategories();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e.response?.data?.message || '分组保存失败');
    } finally {
      setCatSaving(false);
    }
  };

  const handleDeleteCategory = (cat) => {
    api.delete(`/documents/categories/${cat.id}`)
      .then(() => {
        message.success('分组已删除');
        fetchCategories();
      })
      .catch((e) => {
        message.error(e.response?.data?.message || '分组删除失败');
      });
  };

  // ---------- 版本管理 ----------
  const openVersionDrawer = async (record) => {
    setVersionDrawer({ open: true, record, versions: [], loading: true });
    try {
      const resp = await api.get(`/documents/${record.id}/versions`);
      if (resp.data?.success) {
        setVersionDrawer({ open: true, record, versions: resp.data.data.versions || [], loading: false });
      }
    } catch (e) {
      message.error('获取版本记录失败');
      setVersionDrawer((d) => ({ ...d, loading: false }));
    }
  };

  const openVersionModal = (record) => {
    versionForm.resetFields();
    setVersionFileList([]);
    setVersionModal({ open: true, record });
  };

  const handleVersionSubmit = async () => {
    if (versionFileList.length === 0) {
      message.warning('请选择要上传的新版本文件');
      return;
    }
    try {
      const values = await versionForm.validateFields();
      setVersionSaving(true);
      const fd = new FormData();
      fd.append('change_note', values.change_note || '');
      fd.append('file', versionFileList[0].originFileObj || versionFileList[0]);
      const resp = await api.post(`/documents/${versionModal.record.id}/versions`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      message.success(resp.data?.message || '新版本上传成功');
      setVersionModal({ open: false, record: null });
      fetchDocs();
      // 若抽屉开着则刷新
      if (versionDrawer.open) openVersionDrawer(versionModal.record);
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e.response?.data?.message || '新版本上传失败');
      console.error('上传新版本失败:', e);
    } finally {
      setVersionSaving(false);
    }
  };

  // 卡片更多操作菜单
  const buildCardMenu = (record) => ({
    items: [
      { key: 'edit', icon: <EditOutlined />, label: '编辑信息' },
      { key: 'newVersion', icon: <CloudUploadOutlined />, label: '上传新版本' },
      { key: 'versions', icon: <HistoryOutlined />, label: '版本记录' },
      { type: 'divider' },
      { key: 'delete', icon: <DeleteOutlined />, label: '删除文档', danger: true }
    ],
    onClick: ({ key }) => {
      if (key === 'edit') openEditDoc(record);
      if (key === 'newVersion') openVersionModal(record);
      if (key === 'versions') openVersionDrawer(record);
      if (key === 'delete') handleDeleteDoc(record);
    }
  });

  return (
    <div style={{ padding: 24 }}>
      {/* 页面标题与操作区 */}
      <Card styles={{ body: { padding: '20px 24px' } }} style={{ marginBottom: 16 }}>
        <Row justify="space-between" align="middle" gutter={[16, 16]}>
          <Col>
            <Space size={8} align="center">
              <FolderOpenOutlined style={{ fontSize: 22, color: '#1677ff' }} />
              <span style={{ fontSize: 18, fontWeight: 600 }}>文档管理</span>
              <Tag color="blue">统一打印模板</Tag>
            </Space>
            <div style={{ color: '#8c8c8c', fontSize: 13, marginTop: 4 }}>
              所有打印文档以此处最新版本为准，下载前请确认更新时间，避免使用过期文件
            </div>
          </Col>
          {isAdminUser && (
            <Col>
              <Space>
                <Button icon={<FolderOutlined />} onClick={openCategoryModal}>分组管理</Button>
                <Button type="primary" icon={<PlusOutlined />} onClick={openCreateDoc}>新建文档</Button>
              </Space>
            </Col>
          )}
        </Row>

        {/* 分组筛选 + 搜索 */}
        <Row gutter={[12, 12]} align="middle" style={{ marginTop: 16 }}>
          <Col flex="auto">
            <Space size={[8, 8]} wrap>
              <Button
                shape="round"
                type={queryParams.categoryId === null ? 'primary' : 'default'}
                icon={<AppstoreOutlined />}
                onClick={() => setQueryParams((p) => ({ ...p, categoryId: null, page: 1 }))}
              >
                全部
              </Button>
              {categories.map((cat) => (
                <Button
                  key={cat.id}
                  shape="round"
                  type={queryParams.categoryId === cat.id ? 'primary' : 'default'}
                  icon={cat.is_private ? <LockOutlined /> : null}
                  onClick={() => setQueryParams((p) => ({ ...p, categoryId: cat.id, page: 1 }))}
                >
                  {cat.name}
                  <span style={{ opacity: 0.65, marginLeft: 4 }}>{cat.document_count}</span>
                </Button>
              ))}
            </Space>
          </Col>
          <Col>
            <Input
              allowClear
              prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
              placeholder="搜索文档标题 / 备注"
              style={{ width: 260 }}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
            />
          </Col>
        </Row>
      </Card>

      {/* 文档卡片 */}
      <Spin spinning={loading}>
        {docs.length === 0 && !loading ? (
          <Card>
            <Empty description={queryParams.keyword ? '没有找到匹配的文档' : '暂无文档，等待管理员上传'} />
          </Card>
        ) : (
          <Row gutter={[16, 16]}>
            {docs.map((doc) => {
              const meta = getFileTypeMeta(doc.extension);
              return (
                <Col xs={24} sm={12} lg={8} xl={6} key={doc.id} style={{ display: 'flex' }}>
                  <Card
                    hoverable
                    style={{ width: '100%', height: '100%' }}
                    styles={{ body: { padding: 16, display: 'flex', flexDirection: 'column', height: 228 } }}
                  >
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, height: 44 }}>
                      <DocThumbnail doc={doc} fallbackMeta={meta} />
                      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <Text strong ellipsis style={{ fontSize: 15, flex: 1, minWidth: 0, marginBottom: 0 }} title={doc.title}>
                            {doc.title}
                          </Text>
                          {isAdminUser && doc.has_file && (
                            <Tag color="geekblue" style={{ marginInlineEnd: 0, flexShrink: 0 }}>
                              v{doc.version_number}
                            </Tag>
                          )}
                        </div>
                        <div style={{ marginTop: 4, height: 23, lineHeight: '22px', overflow: 'hidden' }}>
                          {doc.category_name && (
                            <Tag
                              color={doc.category_is_private ? 'orange' : 'blue'}
                              icon={doc.category_is_private ? <LockOutlined /> : null}
                              style={{ marginInlineEnd: 0 }}
                            >
                              {doc.category_name}
                            </Tag>
                          )}
                        </div>
                      </div>
                    </div>

                    <div style={{ marginTop: 12, height: 20, lineHeight: '20px', color: '#8c8c8c', fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden' }}>
                      <ClockCircleOutlined /> <span>更新于 {formatTime(doc.updated_at)}</span>
                    </div>

                    <Tooltip title={doc.file_name}>
                      <div style={{
                        marginTop: 6, height: 20, lineHeight: '20px', fontSize: 12, color: '#595959',
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                      }}>
                        <FileTextOutlined /> {doc.file_name || '暂未上传文件'}
                        {doc.file_size ? `（${formatFileSize(doc.file_size)}）` : ''}
                      </div>
                    </Tooltip>

                    {isAdminUser && doc.feishu_url ? (
                      <Tooltip title={doc.feishu_url}>
                        <a
                          href={doc.feishu_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            display: 'block', marginTop: 6, height: 20, lineHeight: '20px',
                            fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                          }}
                        >
                          <LinkOutlined /> 飞书源文件
                        </a>
                      </Tooltip>
                    ) : (
                      <div style={{ height: 20, marginTop: 6 }} />
                    )}

                    <Paragraph
                      type="secondary"
                      ellipsis={{ rows: 2 }}
                      style={{ marginTop: 8, marginBottom: 0, fontSize: 13, height: 40 }}
                    >
                      {doc.remark || '暂无备注'}
                    </Paragraph>

                    <div style={{ flex: 1 }} />
                    <Space style={{ marginTop: 14 }}>
                      <Button
                        type="primary"
                        size="small"
                        icon={<DownloadOutlined />}
                        disabled={!doc.has_file}
                        loading={downloadingId === `/documents/${doc.id}/download`}
                        onClick={() => downloadFile(`/documents/${doc.id}/download`, doc.file_name)}
                      >
                        下载最新版
                      </Button>
                      {isAdminUser && (
                        <Dropdown menu={buildCardMenu(doc)} trigger={['click']} placement="bottomRight">
                          <Button size="small" type="text" icon={<EllipsisOutlined style={{ fontSize: 16 }} />} />
                        </Dropdown>
                      )}
                    </Space>
                  </Card>
                </Col>
              );
            })}
          </Row>
        )}
      </Spin>

      {/* 分页 */}
      {total > PAGE_SIZE && (
        <Row justify="end" style={{ marginTop: 16 }}>
          <Pagination
            current={queryParams.page}
            pageSize={PAGE_SIZE}
            total={total}
            showSizeChanger={false}
            showTotal={(t) => `共 ${t} 个文档`}
            onChange={(page) => setQueryParams((p) => ({ ...p, page }))}
          />
        </Row>
      )}

      {/* 新建/编辑文档弹窗 */}
      <Modal
        title={docModal.mode === 'create' ? '新建文档' : '编辑文档信息'}
        open={docModal.open}
        onCancel={() => setDocModal((m) => ({ ...m, open: false }))}
        onOk={handleDocSubmit}
        confirmLoading={docSaving}
        okText="保存"
        cancelText="取消"
        destroyOnClose
        width={560}
      >
        <Form form={docForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="title" label="文档标题" rules={[{ required: true, message: '请输入文档标题' }]}>
            <Input placeholder="例如：员工入职登记表" maxLength={255} showCount />
          </Form.Item>
          <Form.Item name="category_id" label="所属分组" rules={[{ required: true, message: '请选择分组' }]}>
            <Select placeholder="选择分组" allowClear>
              {categories.map((cat) => (
                <Select.Option key={cat.id} value={cat.id}>
                  {cat.is_private && <LockOutlined style={{ color: '#fa541c', marginRight: 6 }} />}
                  {cat.name}
                  {cat.is_private && <span style={{ color: '#fa541c', marginLeft: 6 }}>（仅管理员）</span>}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="remark" label="备注信息">
            <Input.TextArea rows={3} placeholder="说明文档用途、打印要求或注意事项（前台可见）" maxLength={500} showCount />
          </Form.Item>
          <Form.Item
            name="feishu_url"
            label={<span>飞书链接 <Tag color="default" style={{ marginInlineStart: 4 }}>仅管理员可见</Tag></span>}
            rules={[
              {
                validator: (_, value) => {
                  if (value && !/^https?:\/\/.+/i.test(value.trim())) {
                    return Promise.reject(new Error('请输入以 http:// 或 https:// 开头的链接'));
                  }
                  return Promise.resolve();
                }
              }
            ]}
          >
            <Input
              allowClear
              prefix={<LinkOutlined style={{ color: '#bfbfbf' }} />}
              placeholder="粘贴飞书源文件地址，例如 https://xxx.feishu.cn/file/xxxx"
              maxLength={500}
            />
          </Form.Item>
          {docModal.mode === 'create' && (
            <>
              <div style={{ marginBottom: 8 }}>
                <span style={{ color: '#ff4d4f', marginRight: 4 }}>*</span>
                <label>文档文件</label>
              </div>
              <Upload.Dragger
                fileList={docFileList}
                accept={ACCEPT_EXT}
                maxCount={1}
                beforeUpload={(file) => {
                  setDocFileList([file]);
                  return false;
                }}
                onRemove={() => setDocFileList([])}
              >
                <p className="ant-upload-drag-icon"><InboxOutlined /></p>
                <p className="ant-upload-text">点击或拖拽文件到此区域</p>
                <p className="ant-upload-hint">支持 PDF / Word / Excel / PPT / 图片，单个文件不超过 50MB</p>
              </Upload.Dragger>
              <Form.Item name="change_note" label="版本说明（可选）" style={{ marginTop: 16 }}>
                <Input placeholder="例如：初版，2026年10月启用" maxLength={500} />
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>

      {/* 上传新版本弹窗 */}
      <Modal
        title={`上传新版本 - ${versionModal.record?.title || ''}`}
        open={versionModal.open}
        onCancel={() => setVersionModal({ open: false, record: null })}
        onOk={handleVersionSubmit}
        confirmLoading={versionSaving}
        okText="上传"
        cancelText="取消"
        destroyOnClose
        width={520}
      >
        <div style={{ margin: '12px 0', padding: '8px 12px', background: '#f6f8fa', borderRadius: 6, fontSize: 13, color: '#595959' }}>
          上传后文档将自动更新为最新版本，旧版本仅管理员可在「版本记录」中查看与下载。
        </div>
        <Form form={versionForm} layout="vertical">
          <div style={{ marginBottom: 8 }}>
            <span style={{ color: '#ff4d4f', marginRight: 4 }}>*</span>
            <label>新版本文件</label>
          </div>
          <Upload.Dragger
            fileList={versionFileList}
            accept={ACCEPT_EXT}
            maxCount={1}
            beforeUpload={(file) => {
              setVersionFileList([file]);
              return false;
            }}
            onRemove={() => setVersionFileList([])}
          >
            <p className="ant-upload-drag-icon"><InboxOutlined /></p>
            <p className="ant-upload-text">点击或拖拽文件到此区域</p>
            <p className="ant-upload-hint">支持 PDF / Word / Excel / PPT / 图片，单个文件不超过 50MB</p>
          </Upload.Dragger>
          <Form.Item name="change_note" label="本次更新说明（可选）" style={{ marginTop: 16 }}>
            <Input.TextArea rows={3} placeholder="例如：更新紧急联系人字段、调整表格格式" maxLength={500} showCount />
          </Form.Item>
        </Form>
      </Modal>

      {/* 版本记录抽屉（仅管理员可打开） */}
      <Drawer
        title={`版本记录${versionDrawer.record ? ` - ${versionDrawer.record.title}` : ''}`}
        open={versionDrawer.open}
        width={480}
        onClose={() => setVersionDrawer((d) => ({ ...d, open: false }))}
      >
        <Spin spinning={versionDrawer.loading}>
          <List
            itemLayout="vertical"
            dataSource={versionDrawer.versions}
            locale={{ emptyText: '暂无版本记录' }}
            renderItem={(ver) => (
              <List.Item
                key={ver.id}
                actions={[
                  <Button
                    key="download"
                    type="link"
                    size="small"
                    icon={<DownloadOutlined />}
                    loading={downloadingId === `/documents/versions/${ver.id}/download`}
                    onClick={() => downloadFile(`/documents/versions/${ver.id}/download`, ver.original_name)}
                  >
                    下载此版本
                  </Button>
                ]}
              >
                <List.Item.Meta
                  title={
                    <Space wrap>
                      <Tag color={ver.is_current ? 'green' : 'default'}>v{ver.version_number}</Tag>
                      {ver.is_current === 1 && <Tag color="blue">当前版本</Tag>}
                      <Text type="secondary" style={{ fontSize: 12 }}>{formatTime(ver.created_at)}</Text>
                    </Space>
                  }
                  description={
                    <div>
                      <div style={{ color: '#595959' }}>
                        <FileTextOutlined /> {ver.original_name}
                        {ver.file_size ? `（${formatFileSize(ver.file_size)}）` : ''}
                      </div>
                      <div style={{ marginTop: 4, color: '#8c8c8c', fontSize: 12 }}>
                        上传人：{ver.uploaded_by_name || user?.username || '-'}
                      </div>
                      {ver.change_note && (
                        <div style={{ marginTop: 4, color: '#595959', fontSize: 13 }}>
                          更新说明：{ver.change_note}
                        </div>
                      )}
                    </div>
                  }
                />
              </List.Item>
            )}
          />
        </Spin>
      </Drawer>

      {/* 分组管理弹窗（仅管理员） */}
      <Modal
        title="分组管理"
        open={catModalOpen}
        footer={null}
        onCancel={() => setCatModalOpen(false)}
        width={600}
        destroyOnClose
      >
        <Card size="small" style={{ marginBottom: 12 }}>
          <Form form={catForm} layout="vertical">
            <Row gutter={8}>
              <Col span={12}>
                <Form.Item name="name" label="分组名称" rules={[{ required: true, message: '请输入分组名称' }]} style={{ marginBottom: 8 }}>
                  <Input placeholder="例如：入职表单 / 离职文档" maxLength={100} />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name="sort_order" label="排序（小靠前）" initialValue={0} style={{ marginBottom: 8 }}>
                  <InputNumber min={0} precision={0} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={6} style={{ display: 'flex', alignItems: 'flex-end', gap: 8, paddingBottom: 8 }}>
                <Button type="primary" onClick={handleCategorySubmit} loading={catSaving}>
                  {catEditing ? '保存修改' : '添加分组'}
                </Button>
                {catEditing && <Button onClick={cancelEditCategory}>取消</Button>}
              </Col>
            </Row>
            <Row gutter={8} align="middle">
              <Col span={18}>
                <Form.Item name="description" label="分组描述（可选）" style={{ marginBottom: 0 }}>
                  <Input placeholder="该分组下文档的用途说明" maxLength={500} />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  name="is_private"
                  label="访问范围"
                  initialValue={false}
                  style={{ marginBottom: 0 }}
                  valuePropName="checked"
                  tooltip="开启后该分组及其文档仅管理员可见"
                >
                  <Switch checkedChildren="私密" unCheckedChildren="公开" />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Card>

        <List
          size="small"
          bordered
          dataSource={categories}
          locale={{ emptyText: '暂无分组，请先添加' }}
          renderItem={(cat) => (
            <List.Item
              actions={[
                <Button key="edit" type="link" size="small" onClick={() => startEditCategory(cat)}>编辑</Button>,
                <Popconfirm
                  key="delete"
                  title="确认删除该分组？"
                  description={cat.document_count > 0 ? '分组下仍有文档，无法删除' : '删除后前台将隐藏该分组'}
                  okText="确认"
                  cancelText="取消"
                  okButtonProps={{ danger: true, disabled: cat.document_count > 0 }}
                  onConfirm={() => handleDeleteCategory(cat)}
                >
                  <Button type="link" size="small" danger>删除</Button>
                </Popconfirm>
              ]}
            >
              <List.Item.Meta
                title={(
                  <Space>
                    {cat.is_private
                      ? <LockOutlined style={{ color: '#fa541c' }} />
                      : <FolderOutlined style={{ color: '#1677ff' }} />}
                    {cat.name}
                    <Tag>{cat.document_count} 个文档</Tag>
                    {cat.is_private && <Tag color="orange" icon={<LockOutlined />}>仅管理员</Tag>}
                  </Space>
                )}
                description={cat.description || '无描述'}
              />
            </List.Item>
          )}
        />
      </Modal>
    </div>
  );
};

export default Documents;
